import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useMsal } from '@azure/msal-react';
import { AccountInfo, InteractionRequiredAuthError } from '@azure/msal-browser';
import axios from 'axios';
import { AuthenticationError } from '@/components/auth/AuthenticationError';
import { InsufficientPermissions } from '@/components/auth/InsufficientPermissions';
import { SignInSplash } from '@/components/auth/SignInSplash';
import { getMe } from '@/api/auth/self/self';
import { postUsersProvision } from '@/api/auth/registration/registration';
import { ClientType, setClient } from '@/api/client-registry';
import { env } from '@/env';
import { logger } from '@/services/logger';

interface UserContextType {
  account: AccountInfo;
  error: Error | null;
  isServiceAdmin: boolean;
  switchAccount: (accountId: string) => void;
  availableAccounts: AccountInfo[];
}

const UserContext = createContext<UserContextType | undefined>(undefined);

const SELECTED_ACCOUNT_KEY = 'msal.selectedAccountId';

/**
 * Determines whether the claim set includes the Admin role.
 * @param claims Claim bag returned from MSAL token acquisition.
 * @returns True when an Admin role claim is present; otherwise, false.
 */
function claimsIncludeAdminRole(claims: unknown): boolean {
  console.log('ID Token Claims:', JSON.stringify(claims));
  if (!claims || typeof claims !== 'object') {
    return false;
  }

  const roleClaims = claims as {
    roles?: string[] | string;
    role?: string[] | string;
  };

  const roleValues = roleClaims.roles ?? roleClaims.role;
  if (!roleValues) {
    return false;
  }

  if (Array.isArray(roleValues)) {
    return roleValues.includes('Admin');
  }

  if (typeof roleValues === 'string') {
    return roleValues.split(/[,\s]+/).includes('Admin');
  }

  return false;
}

/**
 * Provider component that manages user authentication state and token acquisition
 */
export function UserProvider({ children }: { children: ReactNode }) {
  const { instance, accounts } = useMsal();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isServiceAdmin, setIsServiceAdmin] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(() => {
    // Load selected account from localStorage on mount
    return localStorage.getItem(SELECTED_ACCOUNT_KEY);
  });

  // Select account based on stored preference, fallback to first account
  const account = (() => {
    if (selectedAccountId) {
      const storedAccount = accounts.find(acc => acc.homeAccountId === selectedAccountId);
      if (storedAccount) {
        return storedAccount;
      }
    }
    return accounts[0] || null;
  })();

  const authScopes = [`api://${env.VITE_AUTH_CLIENT_ID}/access_as_user`, `profile`, `openid`];

  // We do not configure a base url.
  // The SPA and API should always be served from the same origin
  const authApiClient = axios.create({
    baseURL: "/api",
  });

  authApiClient.interceptors.request.use(async (config) => {
    if (!account) {
      throw new Error('No account available');
    }

    const authTokenResponse = await instance.acquireTokenSilent({
      scopes: authScopes,
      account: account,
    });

    config.headers['Authorization'] = `Bearer ${authTokenResponse.accessToken}`;
    return config;
  });

  setClient(ClientType.Auth, authApiClient);

  /**
   * Switch to a different MSAL account
   * @param accountId The homeAccountId of the account to switch to
   */
  const switchAccount = (accountId: string) => {
    const newAccount = accounts.find(acc => acc.homeAccountId === accountId);
    if (newAccount) {
      localStorage.setItem(SELECTED_ACCOUNT_KEY, accountId);
      setSelectedAccountId(accountId);
      logger.trackEvent('UserSession.AccountSwitched', {
        fromAccountId: account?.homeAccountId,
        toAccountId: accountId,
        username: newAccount.username,
      });
    }
  };

  /**
   * Initialize user by looking up their profile, provisioning if needed
   */
  const initializeUser = async () => {
    if (!account) {
      setIsServiceAdmin(false);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const authTokenResponse = await instance.acquireTokenSilent({
        scopes: authScopes,
        account,
      });
      setIsServiceAdmin(claimsIncludeAdminRole(authTokenResponse.idTokenClaims) || env.VITE_ENVIRONMENT === 'simulated');

      // Try to get user profile
      try {
        await getMe();
      } catch (userLookupErr) {
        // User lookup failed, try to provision
        console.log('User lookup failed, provisioning user:', userLookupErr);
        logger.info('User lookup failed, provisioning user', {
          accountId: account.homeAccountId,
        });

        try {
          await postUsersProvision({
            email: account.username,
            firstName: account.idTokenClaims?.given_name as string || '',
            lastName: account.idTokenClaims?.family_name as string || '',
          });
        } catch (provisionErr) {
          // Provisioning failed - show error and ask user to sign in again
          const error = new Error('We were not able to sign you in. Please try again.');
          setError(error);
          logger.error(provisionErr instanceof Error ? provisionErr : new Error('User provisioning failed'), {
            context: 'UserProvisioning',
            accountId: account.homeAccountId,
          });
          console.error('User provisioning failed:', provisionErr);
          return;
        }
      }
    } catch (err) {
      setIsServiceAdmin(false);
      if (err instanceof InteractionRequiredAuthError) {
        // If silent token acquisition fails, we could trigger interactive auth
        // For now, we'll just set the error
        const error = new Error('Interactive authentication required');
        setError(error);
        logger.warn('Interactive authentication required', {
          accountId: account?.homeAccountId,
        });
      } else {
        const error = err instanceof Error ? err : new Error('User initialization failed');
        setError(error);
        logger.error(error, {
          context: 'UserInitialization',
          accountId: account?.homeAccountId,
        });
      }
      console.error('User initialization failed:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Initialize user on mount and when account changes
  useEffect(() => {
    initializeUser();
  }, [account?.homeAccountId]);

  // Track user session in Application Insights
  useEffect(() => {
    if (account) {
      // Set user context in Application Insights
      logger.setUser(account.homeAccountId, account.tenantId);

      // Track session start event
      logger.trackEvent('UserSession.Started', {
        accountId: account.homeAccountId,
        username: account.username,
        tenantId: account.tenantId,
        isServiceAdmin,
        environment: import.meta.env.MODE,
      });

      // Clean up: clear user context on unmount
      return () => {
        logger.clearUser();
      };
    }
  }, [account?.homeAccountId, isServiceAdmin]);

  if (isLoading) {
    return <SignInSplash />;
  }

  if (!account || error) {
    return <AuthenticationError />;
  }
  
  if (!isServiceAdmin) {
    return <InsufficientPermissions />;
  }

  return (
    <UserContext.Provider
      value={{
        account,
        error,
        isServiceAdmin,
        switchAccount,
        availableAccounts: accounts,
      }}
    >
      {(isLoading) ? <div className="min-h-screen flex items-center justify-center">Loading...</div> : children}
    </UserContext.Provider>
  );
}

/**
 * Hook to access user context
 * @throws Error if used outside of UserProvider
 */
export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}
